import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Heading } from "@/components/ui/heading";
import { Text } from "@/components/ui/text";
import Container from "../Container";
import Footer from "../Footer";

const TermsOfServicePage: React.FunctionComponent = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Container className="pt-28 pb-16">
        <div className="max-w-3xl">
          <Heading level={1} variant="lg" className="mb-2">
            Terms of Service
          </Heading>
          <Text variant="sm" className="text-muted-foreground mb-10">
            Effective date: 30 March 2026
          </Text>

          {/* 1. Introduction */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            1. Introduction
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            These terms of service (&quot;Terms&quot;) govern your access to and
            use of the Punch Cards service (&quot;Service&quot;), operated by
            Punch Cards Ltd, a company registered in England and Wales
            (&quot;we&quot;, &quot;us&quot;, &quot;our&quot;).
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            By accessing or using the Service, you agree to be bound by these
            Terms. If you are using the Service on behalf of an organisation,
            you represent and warrant that you have the authority to bind that
            organisation to these Terms.
          </Text>

          {/* 2. Definitions */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            2. Definitions
          </Heading>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
            <li>
              <Text variant="md">
                <strong>&quot;Service&quot;</strong> means the Punch Cards web
                application and any related services provided by us.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>&quot;User&quot;</strong> means any individual or entity
                that accesses or uses the Service.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>&quot;Workspace&quot;</strong> means a company account
                within the Service, associated with a single organisation.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>&quot;Connected Platforms&quot;</strong> means third-party
                messaging or agent systems (such as Slack and Intercom)
                integrated with the Service.
              </Text>
            </li>
            <li>
              <Text variant="md">
                <strong>&quot;Scores&quot;</strong> means the performance metrics
                and ratings generated by the Service based on aggregate data from
                Connected Platforms.
              </Text>
            </li>
          </ul>

          {/* 3. Account Registration */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            3. Account Registration
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            To use the Service, you must register an account and provide
            accurate, complete, and current information. You are responsible for
            maintaining the confidentiality of your account credentials and for
            all activities that occur under your account.
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            You must notify us immediately at{" "}
            <a
              href="mailto:privacy@punch.cards"
              className="text-foreground underline hover:text-highlight"
            >
              privacy@punch.cards
            </a>{" "}
            if you become aware of any unauthorised use of your account.
          </Text>

          {/* 4. The Service */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            4. The Service
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            Punch Cards provides employee performance scoring based on aggregate
            metrics retrieved from your Connected Platforms. Scores are generated
            from numerical action counts, including reply counts, ticket closure
            counts, and customer satisfaction (CSAT) ratings.
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            The Service does <strong>not</strong> access, process, or store the
            content of customer support tickets, internal messages, or any
            personal data belonging to your customers or end users. Our
            integrations retrieve only the minimum data necessary to generate
            Scores.
          </Text>

          {/* 5. Connected Platform Integrations */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            5. Connected Platform Integrations
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            You must have appropriate authority within your organisation to
            connect your company&apos;s Slack, Intercom, or other supported
            platform accounts to the Service.
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            You are responsible for ensuring that your use of Punch Cards
            complies with your own agreements with each Connected Platform,
            including their terms of service and acceptable use policies. Punch
            Cards accesses Connected Platforms solely through their official APIs
            and requests only the minimum permissions required.
          </Text>

          {/* 6. Subscription and Payment */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            6. Subscription and Payment
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            Access to the Service requires a paid subscription. Subscription
            fees are as set out on our website or as otherwise agreed in writing.
          </Text>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
            <li>
              <Text variant="md">
                Fees are payable in advance on a monthly or annual basis, as
                selected at the time of subscription.
              </Text>
            </li>
            <li>
              <Text variant="md">
                Subscriptions renew automatically at the end of each billing
                period unless cancelled before the renewal date.
              </Text>
            </li>
            <li>
              <Text variant="md">
                You may cancel your subscription at any time. Cancellation takes
                effect at the end of the current billing period. No refunds are
                provided for partial billing periods, except where required by
                applicable law.
              </Text>
            </li>
            <li>
              <Text variant="md">
                We reserve the right to change our fees upon 30 days&apos; written
                notice. Continued use of the Service after a fee change
                constitutes acceptance of the new fees.
              </Text>
            </li>
          </ul>
          <Text variant="md" className="text-muted-foreground mb-4">
            All payments are processed by a third-party payment provider. We do
            not directly store your payment card details.
          </Text>

          {/* 7. Acceptable Use */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            7. Acceptable Use
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            You agree not to:
          </Text>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
            <li>
              <Text variant="md">
                Use the Service for any unlawful purpose or in violation of any
                applicable law or regulation.
              </Text>
            </li>
            <li>
              <Text variant="md">
                Attempt to reverse engineer, decompile, or disassemble any part
                of the Service.
              </Text>
            </li>
            <li>
              <Text variant="md">
                Gain or attempt to gain unauthorised access to the Service, other
                accounts, or any related systems or networks.
              </Text>
            </li>
            <li>
              <Text variant="md">
                Use Scores or data from the Service as the sole basis for
                employment decisions that would constitute unlawful
                discrimination under the Equality Act 2010 or any other
                applicable legislation.
              </Text>
            </li>
            <li>
              <Text variant="md">
                Interfere with or disrupt the integrity or performance of the
                Service.
              </Text>
            </li>
          </ul>

          {/* 8. Intellectual Property */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            8. Intellectual Property
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            All intellectual property rights in the Service, including its
            design, software, algorithms, and documentation, belong to Punch
            Cards Ltd or its licensors. Nothing in these Terms transfers any
            intellectual property rights to you, except for the limited right to
            use the Service in accordance with these Terms.
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            You retain all rights in the data you provide to or generate through
            the Service. You grant us a limited licence to process your data
            solely for the purpose of providing the Service.
          </Text>

          {/* 9. Data Protection */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            9. Data Protection
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            Our processing of personal data is governed by our{" "}
            <Link
              to="/privacy"
              className="text-foreground underline hover:text-highlight"
            >
              Privacy Policy
            </Link>
            , which forms part of these Terms.
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            Where Punch Cards processes personal data on your behalf as a data
            processor, a Data Processing Agreement is available upon request by
            contacting{" "}
            <a
              href="mailto:privacy@punch.cards"
              className="text-foreground underline hover:text-highlight"
            >
              privacy@punch.cards
            </a>
            .
          </Text>

          {/* 10. Availability and Support */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            10. Availability and Support
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            We use reasonable efforts to maintain the availability of the
            Service, but do not guarantee uninterrupted or error-free access. We
            may carry out planned maintenance from time to time and will
            endeavour to provide reasonable advance notice.
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            No service level agreement (SLA) applies unless separately agreed in
            writing.
          </Text>

          {/* 11. Limitation of Liability */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            11. Limitation of Liability
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            Nothing in these Terms excludes or limits our liability for:
          </Text>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
            <li>
              <Text variant="md">
                Death or personal injury caused by our negligence.
              </Text>
            </li>
            <li>
              <Text variant="md">Fraud or fraudulent misrepresentation.</Text>
            </li>
            <li>
              <Text variant="md">
                Any other liability that cannot be excluded or limited under the
                laws of England and Wales.
              </Text>
            </li>
          </ul>
          <Text variant="md" className="text-muted-foreground mb-4">
            Subject to the above, to the maximum extent permitted by law:
          </Text>
          <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
            <li>
              <Text variant="md">
                We shall not be liable for any indirect, incidental, special,
                consequential, or punitive damages, or any loss of profits,
                revenue, data, or business opportunity.
              </Text>
            </li>
            <li>
              <Text variant="md">
                Our total aggregate liability arising out of or in connection
                with these Terms or the Service shall not exceed the total fees
                paid by you to us in the twelve (12) months immediately
                preceding the event giving rise to the claim.
              </Text>
            </li>
          </ul>

          {/* 12. Indemnification */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            12. Indemnification
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            You agree to indemnify and hold harmless Punch Cards Ltd, its
            directors, employees, and agents from and against any claims,
            liabilities, damages, losses, and expenses (including reasonable
            legal fees) arising out of or in connection with your use of the
            Service, your breach of these Terms, or your violation of any
            applicable law or the rights of any third party.
          </Text>

          {/* 13. Termination */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            13. Termination
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            Either party may terminate these Terms by providing 30 days&apos;
            written notice to the other party.
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            We may suspend or terminate your access to the Service immediately
            if you breach these Terms or if we reasonably believe that your use
            of the Service poses a risk to the Service, other users, or third
            parties.
          </Text>
          <Text variant="md" className="text-muted-foreground mb-4">
            Upon termination, your right to use the Service ceases immediately.
            Your data will be handled in accordance with our{" "}
            <Link
              to="/privacy"
              className="text-foreground underline hover:text-highlight"
            >
              Privacy Policy
            </Link>
            .
          </Text>

          {/* 14. Changes to Terms */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            14. Changes to These Terms
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            We may update these Terms from time to time. We will provide at
            least 30 days&apos; notice of any material changes by email or
            through a prominent notice within the Service. Your continued use of
            the Service after the updated Terms take effect constitutes your
            acceptance of the changes.
          </Text>

          {/* 15. Governing Law */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            15. Governing Law and Jurisdiction
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            These Terms are governed by and construed in accordance with the laws
            of England and Wales. The courts of England and Wales shall have
            exclusive jurisdiction to settle any dispute arising out of or in
            connection with these Terms.
          </Text>

          {/* 16. Contact */}
          <Heading level={2} variant="sm" className="mt-10 mb-4">
            16. Contact Us
          </Heading>
          <Text variant="md" className="text-muted-foreground mb-4">
            If you have any questions about these Terms, please contact us at{" "}
            <a
              href="mailto:privacy@punch.cards"
              className="text-foreground underline hover:text-highlight"
            >
              privacy@punch.cards
            </a>
            .
          </Text>
        </div>
      </Container>
      <Footer />
    </>
  );
};

export default TermsOfServicePage;
